export type StorageAdapter = {
  read: (name: string) => string | undefined;
  remove: (name: string) => void;
  subscribe: (name: string, listener: () => void) => () => void;
  write: (name: string, raw: string) => void;
};

const COOKIE_MAX_AGE = 31536000;

const webStorage = (pick: () => Storage | undefined): StorageAdapter => ({
  read: name => {
    try {
      return pick()?.getItem(name) ?? undefined;
    } catch {
      return undefined;
    }
  },
  remove: name => {
    try {
      pick()?.removeItem(name);
    } catch {
      return;
    }
  },
  subscribe: (name, listener) => {
    if (typeof window === 'undefined') return () => undefined;
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key === name || event.key.startsWith(`${name}:`)) listener();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  },
  write: (name, raw) => {
    try {
      pick()?.setItem(name, raw);
    } catch {
      return;
    }
  },
});

export const localSink = webStorage(() => (typeof localStorage === 'undefined' ? undefined : localStorage));

export const sessionSink = webStorage(() => (typeof sessionStorage === 'undefined' ? undefined : sessionStorage));

export const cookieSink: StorageAdapter = {
  read: name => {
    if (typeof document === 'undefined') return undefined;
    for (const entry of document.cookie.split(';')) {
      const separator = entry.indexOf('=');
      if (separator === -1) continue;
      if (entry.slice(0, separator).trim() !== name) continue;
      return decodeURIComponent(entry.slice(separator + 1));
    }
    return undefined;
  },
  remove: name => {
    if (typeof document === 'undefined') return;
    document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
  },
  subscribe: () => () => undefined,
  write: (name, raw) => {
    if (typeof document === 'undefined') return;
    const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${encodeURIComponent(raw)}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
  },
};
